<script lang="ts">
    import FileUploaderManager from "@modules/file_uploader/FileUploaderManager/FileUploaderManager";
    import CN from "classnames";
    import { createEventDispatcher, onMount } from "svelte";

    export let fileUploaderManager: FileUploaderManager;
    export let acceptedFileExt: string[] = [];
    export let multiple = false;
    let inputRef: HTMLInputElement;
    let borderDance = false;

    export const clickInput = () => {
        inputRef && inputRef.click();
    };

    const dispatch = createEventDispatcher();

    const uploadFiles = (fileList: FileList | null | undefined) => {
        if (fileList) {
            const files = Array.from(fileList);
            fileUploaderManager.addFiles(files);
        }
    };

    const handleDrop = (event: DragEvent) => {
        borderDance = false;
        uploadFiles(event.dataTransfer?.files);
    };

    const handleInputChange = (event: Event) => {
        const target = event.target as HTMLInputElement;
        const fileList = target.files;
        uploadFiles(fileList);
    };

    const handleDragOver = () => (borderDance = true);
    const handleDragLeave = () => (borderDance = false);

    onMount(() => {
        const uploadedFilesSubscription =
            fileUploaderManager.uploadedFiles$.subscribe((uploadedFiles) => {
                dispatch("change", {
                    value: uploadedFiles.map((file) => ({
                        key: file.key,
                        bucket: file.bucket,
                    })),
                });
            });

        return () => {
            uploadedFilesSubscription.unsubscribe();
        };
    });
</script>

<div
    class={CN("gw-file-uploader", {
        "gw-file-uploader--border-dance": borderDance,
    })}
    on:drop|preventDefault|stopPropagation={handleDrop}
    on:dragenter|preventDefault|stopPropagation={handleDragOver}
    on:dragover|preventDefault|stopPropagation={handleDragOver}
    on:dragleave|preventDefault|stopPropagation={handleDragLeave}
>
    <slot />
    <input
        tabindex="-1"
        bind:this={inputRef}
        on:change={handleInputChange}
        accept={acceptedFileExt.map((ext) => `image/${ext}`).join(", ") || ""}
        class="gw-hide-elements"
        type="file"
        {multiple}
    />
</div>

<style lang="postcss">
    .gw-file-uploader {
        --gw-file-uploader-color-black: #262842;
        --gw-file-uploader-color-white: #ffffff;
        --gw-file-uploader-color-gray-bg: #f7f9fd;
        --gw-file-uploader-color-gray-text: #8b8c9e;

        box-sizing: border-box;
        border: 1px dashed var(--gw-file-uploader-color-black);
        width: 100%;
        min-height: var(--gw-file-uploader-height, 10em);
        height: fit-content;
        background-color: var(--gw-file-uploader-color-gray-bg);
        border-radius: 12px;

        &--border-dance {
            border: 1px solid transparent;
            background-image: linear-gradient(
                    90deg,
                    silver 50%,
                    transparent 50%
                ),
                linear-gradient(90deg, silver 50%, transparent 50%),
                linear-gradient(0deg, silver 50%, transparent 50%),
                linear-gradient(0deg, silver 50%, transparent 50%);
            background-repeat: repeat-x, repeat-x, repeat-y, repeat-y;
            background-size: 10px 1px, 10px 1px, 1px 10px, 1px 10px;
            background-position: left top, right bottom, left bottom, right top;
            animation: border-dance 1s infinite linear;
        }

        @keyframes border-dance {
            0% {
                background-position: left top, right bottom, left bottom,
                    right top;
            }
            100% {
                background-position: left 10px top, right 10px bottom,
                    left bottom 10px, right top 10px;
            }
        }
    }
</style>
